import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

// Masked line-by-line reveal — each line slides up out of an overflow mask.
export function MaskedLines({ lines, className = "", as: Tag = "h1", delay = 0 }) {
  return (
    <Tag className={`masked-lines ${className}`}>
      {lines.map((line, i) => (
        <span className="line-mask" key={i}>
          <motion.span
            className="line-inner"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: delay + i * 0.13 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

// Scroll-triggered fade/slide reveal for sections and cards.
export function Reveal({ children, className = "", delay = 0, y = 42 }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-12% 0px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

// Gentle parallax — content drifts against scroll direction.
export function Parallax({ children, className = "", speed = 0.12 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`${speed * 100}%`, `${speed * -100}%`]);
  return (
    <div ref={ref} className={className} style={{ overflow: "hidden" }}>
      <motion.div style={{ y, height: "100%" }}>{children}</motion.div>
    </div>
  );
}

// Chapter heading — numbered manifesto style.
export function Chapter({ index, eyebrow, title, text, light = false, center = false }) {
  return (
    <Reveal className={`chapter-head ${light ? "light" : ""} ${center ? "center" : ""}`}>
      <div className="chapter-meta">
        <span className="chapter-index">{index}</span>
        <span className="chapter-rule" />
        <span className="eyebrow">{eyebrow}</span>
      </div>
      <h2>{title}</h2>
      {text && <p>{text}</p>}
    </Reveal>
  );
}
